import { effect, signal, Signal } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { RoomsResultsService } from 'src/app/core/services/rooms-results/rooms-results.service';
import { RoomStatus } from 'src/app/shared/types/room-status';
import { Countdown } from 'src/app/shared/utils/app.utils';

/**
 * Creates a signal that tracks the formatted countdown string for an unfinished room.
 * Must be called in an injection context (component constructor or field initializer).
 */
export function createUnfinishedCountdown(
  roomsResultsService: RoomsResultsService,
  translocoService: TranslocoService,
): Signal<string> {
  const timeRemaining = signal<string>('');
  let timer: ReturnType<typeof setInterval> | null = null;

  const stop = () => {
    if (timer !== null) {
      clearInterval(timer);
      timer = null;
    }
  };

  const start = (room: { status(): RoomStatus; deadline(): number }) => {
    const tick = () => {
      const secondsLeft = Math.round((room.deadline() - Date.now()) / 1000);
      const status = room.status();
      if (status === RoomStatus.Start) {
        timeRemaining.set(
          translocoService.translate('components.timeout-label.status-start', {
            timeRemaining: Countdown.asTimeLeft(secondsLeft),
          }),
        );
      } else if (status === RoomStatus.Finish) {
        timeRemaining.set(
          translocoService.translate('components.timeout-label.status-finish', {
            timeRemaining: Countdown.asTimeLeft(secondsLeft),
          }),
        );
      } else {
        timeRemaining.set('');
      }
    };
    tick();
    timer = setInterval(tick, 1000);
  };

  effect((onCleanup) => {
    const room = roomsResultsService.unfinishedRoom();
    stop();
    if (room) {
      start(room);
    }
    onCleanup(() => stop());
  });

  return timeRemaining.asReadonly();
}
